if (!customElements.get('sticky-atc-panel')) {
  // Inherit the theme's primary button styling so the sticky bar cannot drift
  // from the main Add to cart button, which is a two-tone diagonal gradient
  // with a 2px border rather than a flat colour.
  const BTN_CLASS = 'btn btn--primary sticky-atc-panel__btn';

  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();
      this.productSection = this.closest('.cc-main-product');
    }

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.buildButton();
      this.bindAddToCart();
      this.reveal();
    }

    /**
     * The bar is shown from page load rather than revealed on scroll, so the
     * hidden state the section renders is cleared as soon as the element
     * upgrades. Leaving those classes in the markup means the bar stays
     * hidden if this script ever fails to load, rather than rendering broken.
     */
    reveal() {
      this.classList.remove('sticky-atc-panel--out', 'invisible');
    }

     /**
     * Returns the Add to cart button the shopper would actually press.
     *
     * On a product with accessories there are TWO [name="add"] buttons in the
     * section. sf9__product_accessories.js clones the original into
     * .product-accessories__container and neutralises the original in place
     * (opacity 0, height 0, pointer-events none) - so the visible control, and
     * the one wired to add the selected accessories at their chosen
     * quantities, is the clone.
     *
     * A plain querySelector('[name="add"]') returns the FIRST match in DOM
     * order, which is the neutralised original. Clicking that adds the product
     * alone and silently drops every accessory the shopper selected. So the
     * clone is preferred, and the original is the fallback for products with
     * no accessories, where the clone does not exist.
     */
    mainButton() {
      if (!this.productSection) return null;

      return (
        this.productSection.querySelector('.custom__single-add-to-cart') ||
        this.productSection.querySelector('[name="add"]')
      );
    }

    /**
     * Copies the cart icon from the main Add to cart button so the sticky bar
     * matches it exactly and stays in sync if the theme's icon ever changes.
     */
    addIcon(button) {
      if (button.querySelector('svg')) return;
      const mainAtc = this.mainButton();
      const icon = mainAtc ? mainAtc.querySelector('svg') : null;
      if (icon) button.prepend(icon.cloneNode(true));
    }

    /**
     * The section may still render the legacy card markup: thumbnail, title,
     * price and a "View details" anchor. Strip that back to a single control
     * and turn it into a real Add to cart button. If the section has already
     * been updated to render the button directly, only styling and the icon
     * are applied.
     */
    buildButton() {
      const existing = this.querySelector('[data-sticky-atc-button]');
      if (existing) {
        existing.className = BTN_CLASS;
        this.addIcon(existing);
        return;
      }

      this.querySelectorAll('.card__media, .card__info').forEach((el) => el.remove());

      const link = this.querySelector('.card__buttons a');
      if (!link) return;

      const mainAtc = this.mainButton();
      if (!mainAtc) {
        // No form to proxy - leave the anchor as a scroll link, styled to match.
        link.className = BTN_CLASS;
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = BTN_CLASS;
      button.setAttribute('data-sticky-atc-button', '');

      const label = document.createElement('span');
      label.textContent = (mainAtc.textContent || '').trim() || 'Add to cart';
      button.appendChild(label);

      this.addIcon(button);
      link.replaceWith(button);
    }

    /**
     * Because the sticky button proxies the main Add to cart, the standard
     * add_to_cart event fires identically for both. This separate event is
     * what lets you tell them apart in GA4: compare sticky_atc_click counts
     * against total add_to_cart. Fires on click, so it counts intent - an add
     * that then fails server-side is still counted here.
     */
    trackClick() {
      const form = this.productSection
        ? this.productSection.querySelector('form[action*="/cart/add"]')
        : null;
      const variantInput = form ? form.querySelector('[name="id"]') : null;
      const qtyInput = form ? form.querySelector('[name="quantity"]') : null;

      const payload = {
        variant_id: variantInput ? variantInput.value : null,
        quantity: qtyInput ? qtyInput.value : '1'
      };

      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'sticky_atc_click', sticky_atc: payload });
      } catch (e) {
        /* analytics must never block the add to cart */
      }

      try {
        if (typeof window.clarity === 'function') {
          window.clarity('set', 'sticky_atc_click', 'true');
        }
      } catch (e) {
        /* no-op */
      }

      try {
        if (typeof window.hj === 'function') {
          window.hj('event', 'sticky_atc_click');
        }
      } catch (e) {
        /* no-op */
      }
    }

    /**
     * Proxies the sticky button to the main product form button, so quantity,
     * selected variant and line item properties (e.g. old appliance
     * collection) all travel with the request. The main button is looked up at
     * click time because the form re-renders on variant change.
     */
    bindAddToCart() {
      const stickyButton = this.querySelector('[data-sticky-atc-button]');
      if (!stickyButton) return;

      stickyButton.addEventListener('click', () => {
        const mainAtc = this.mainButton();
        if (!mainAtc || mainAtc.disabled) return;

        this.trackClick();
        mainAtc.click();
      });
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}